import { useEffect, useState } from 'react';
import { PageFrame } from '../components/PageFrame';
import { createLearnerSessionClient } from '../platform/learner/learner-session-client';

type Props = {
  client?: Pick<ReturnType<typeof createLearnerSessionClient>, 'exit'>;
};

// Module-level, not a default-parameter expression: a default evaluated in
// the parameter list is a fresh object on every render this component makes
// without an explicit `client` prop, which changes the useEffect below's
// `[client]` dependency identity on every render and reruns exit() again.
const defaultClient = createLearnerSessionClient();

export function SafeExit({ client = defaultClient }: Props) {
  // DM-IMP-005 changes-required remediation, Task 4: a third, honest
  // 'failed' status replaces the old design's assumption that exit()
  // never throws — it always resolved successfully in the old code no
  // matter what the server actually did, so this screen unconditionally
  // claimed "finished safely" even after a genuine revocation failure.
  // exit() now throws on a confirmed server-side failure (matching this
  // codebase's other client methods' own check-response.ok-and-throw
  // convention) or on a thrown network/infra fault — both land here.
  const [status, setStatus] = useState<'exiting' | 'done' | 'failed'>(
    'exiting',
  );

  useEffect(() => {
    let active = true;
    void client
      .exit()
      .then(() => {
        if (!active) return;
        // Replaces this already-/safe-exit entry in place, purely to avoid
        // leaving a duplicate "exiting" entry in history. It does NOT by
        // itself protect a prior learner's session state from Back — every
        // earlier learner route is wrapped in RequireLearnerCapability,
        // which re-checks the (now server-side-revoked) capability on
        // mount and redirects to the neutral /learn screen; that guard is
        // the actual Back-navigation defense, not this call.
        window.history.replaceState(null, '', '/safe-exit');
        setStatus('done');
      })
      .catch(() => {
        if (!active) return;
        // The learner-facing BFF route (learner-exit.ts) always clears the
        // browser-held cookie in its response regardless of whether
        // server-side revocation itself succeeded — so the device is
        // locally safe either way. This screen must not claim the server
        // confirmed revocation unless it genuinely did; render an honest,
        // calm failure state instead of a false "finished safely" claim.
        window.history.replaceState(null, '', '/safe-exit');
        setStatus('failed');
      });
    return () => {
      active = false;
    };
  }, [client]);

  if (status === 'exiting') {
    return (
      <PageFrame title="Finishing up" audience="learner">
        <p role="status" className="mt-4 text-lg">
          Finishing up…
        </p>
      </PageFrame>
    );
  }

  if (status === 'failed') {
    return (
      <PageFrame title="This device is safe to close" audience="learner">
        <p role="alert" className="mt-4 text-lg">
          This browser has been cleared. We could not confirm the session was
          fully closed on our end, but no learner details are shown here — you
          may still close this shared-device window.
        </p>
      </PageFrame>
    );
  }

  return (
    <PageFrame title="You have finished safely" audience="learner">
      <p role="status" className="mt-4 text-lg">
        No learner details are shown. You may close this shared-device window.
      </p>
    </PageFrame>
  );
}
